import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import Header from "components/headers/light.js";
import Footer from "components/footers/FiveColumnWithBackground";
import { SectionHeading } from "components/misc/Headings.js";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons.js";
import { ReactComponent as MapPinIcon } from "feather-icons/dist/icons/map-pin.svg";
import { ReactComponent as MailIcon } from "feather-icons/dist/icons/mail.svg";
import { ReactComponent as PhoneIcon } from "feather-icons/dist/icons/phone.svg";

const Container = tw.div`relative max-w-screen-xl mx-auto px-4 sm:px-8 py-12 md:py-20`;
const Heading = tw(SectionHeading)`w-full mb-12`;
const TwoColumn = tw.div`flex flex-col lg:flex-row gap-12 lg:gap-16`;

const InfoColumn = tw.div`w-full lg:w-2/5`;
const FormColumn = tw.div`w-full lg:w-3/5`;

const InfoCard = tw.div`mb-8 flex items-start`;
const InfoIcon = tw.div`flex-shrink-0 w-12 h-12 rounded-full bg-primary-100 text-primary-500 flex items-center justify-center mr-4`;
const InfoContent = tw.div``;
const InfoTitle = tw.h4`font-bold text-lg text-gray-900`;
const InfoText = tw.p`mt-1 text-secondary-100 leading-relaxed`;
const InfoLink = tw.a`text-primary-500 font-medium hover:text-primary-700 transition duration-300`;

const FormContainer = styled.div`
  ${tw`p-8 sm:p-10 bg-gray-100 rounded-lg`}
  form {
    ${tw`mt-2`}
  }
  h3 {
    ${tw`text-2xl font-bold text-gray-900 mb-6`}
  }
  input, textarea {
    ${tw`w-full bg-white border border-gray-300 rounded-lg px-4 py-3 text-gray-900 text-base focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition duration-200`};
    ::placeholder {
      ${tw`text-gray-400`}
    }
  }
`;
const InputGroup = tw.div`mb-5`;
const Label = tw.label`block text-sm font-semibold text-gray-700 mb-2`;
const Input = tw.input``;
const TextArea = tw.textarea`h-32 resize-none`;
const SubmitButton = tw(PrimaryButtonBase)`w-full sm:w-auto mt-2 rounded-full`;
const ErrorText = tw.p`mt-4 text-sm text-red-600 font-medium`;
const Honeypot = styled.div`
  ${tw`absolute opacity-0 h-0 w-0 overflow-hidden`}
  input {
    ${tw`absolute`}
  }
`;

const MapSection = tw.div`mt-16`;
const MapHeading = tw.h3`text-2xl font-bold text-gray-900 mb-6 text-center`;
const MapWrapper = tw.div`rounded-lg overflow-hidden shadow-lg`;

const initialForm = {
  name: "",
  email: "",
  subject: "",
  message: "",
  _gotcha: "",
};

export default () => {
  const history = useHistory();
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Bot honeypot — pretend success without calling API
    if (form._gotcha) {
      history.push("/thank-you");
      return;
    }

    const name = form.name.trim();
    const email = form.email.trim();
    const subject = form.subject.trim();
    const message = form.message.trim();

    if (!name || !email || !message) {
      setError("Please fill in your name, email, and message.");
      return;
    }

    setSubmitting(true);
    try {
      const contactUrl = process.env.REACT_APP_CONTACT_API_URL;
      if (!contactUrl) {
        throw new Error("Contact API is not configured");
      }

      const res = await fetch(contactUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          subject,
          message,
          userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Request failed");
      }

      history.push("/thank-you");
    } catch (err) {
      console.error("Contact form submit failed:", err);
      setError("Something went wrong sending your message. Please try again or email info@spurnest.com.");
      setSubmitting(false);
    }
  };

  return (
    <AnimationRevealPage>
      <Header />
      <Container>
        <Heading>Get In Touch</Heading>
        <TwoColumn>
          <InfoColumn>
            <InfoCard>
              <InfoIcon><MapPinIcon tw="w-5 h-5" /></InfoIcon>
              <InfoContent>
                <InfoTitle>Gandhinagar Office</InfoTitle>
                <InfoText>
                  C/404, Siddheshwari Heights<br />
                  Gandhinagar Mahudi Highway, Pehtapur<br />
                  Gandhinagar, Gujarat 382610
                </InfoText>
              </InfoContent>
            </InfoCard>
            <InfoCard>
              <InfoIcon><MailIcon tw="w-5 h-5" /></InfoIcon>
              <InfoContent>
                <InfoTitle>Email</InfoTitle>
                <InfoText>
                  <InfoLink href="mailto:info@spurnest.com">info@spurnest.com</InfoLink>
                </InfoText>
              </InfoContent>
            </InfoCard>
            <InfoCard>
              <InfoIcon><PhoneIcon tw="w-5 h-5" /></InfoIcon>
              <InfoContent>
                <InfoTitle>Phone</InfoTitle>
                <InfoText>
                  <InfoLink href="tel:+919726816391">+91 97268 16391</InfoLink>
                  <br />
                  <InfoLink href="tel:+918460407942">+91 84604 07942</InfoLink>
                </InfoText>
              </InfoContent>
            </InfoCard>
          </InfoColumn>

          <FormColumn>
            <FormContainer>
              <h3>Send Us a Message</h3>
              <form onSubmit={onSubmit} noValidate>
                <Honeypot aria-hidden="true">
                  <Label htmlFor="_gotcha">Company</Label>
                  <Input
                    id="_gotcha"
                    type="text"
                    name="_gotcha"
                    value={form._gotcha}
                    onChange={onChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </Honeypot>
                <InputGroup>
                  <Label htmlFor="name">Your Name</Label>
                  <Input
                    id="name"
                    type="text"
                    name="name"
                    placeholder="John Doe"
                    value={form.name}
                    onChange={onChange}
                    required
                    maxLength={119}
                    disabled={submitting}
                  />
                </InputGroup>
                <InputGroup>
                  <Label htmlFor="email">Your Email</Label>
                  <Input
                    id="email"
                    type="email"
                    name="email"
                    placeholder="john@example.com"
                    value={form.email}
                    onChange={onChange}
                    required
                    maxLength={199}
                    disabled={submitting}
                  />
                </InputGroup>
                <InputGroup>
                  <Label htmlFor="subject">Subject</Label>
                  <Input
                    id="subject"
                    type="text"
                    name="subject"
                    placeholder="Project inquiry"
                    value={form.subject}
                    onChange={onChange}
                    maxLength={199}
                    disabled={submitting}
                  />
                </InputGroup>
                <InputGroup>
                  <Label htmlFor="message">Message</Label>
                  <TextArea
                    id="message"
                    name="message"
                    placeholder="Tell us about your project..."
                    value={form.message}
                    onChange={onChange}
                    required
                    maxLength={4999}
                    disabled={submitting}
                  />
                </InputGroup>
                <SubmitButton type="submit" disabled={submitting}>
                  {submitting ? "Sending…" : "Send Message"}
                </SubmitButton>
                {error && <ErrorText>{error}</ErrorText>}
              </form>
            </FormContainer>
          </FormColumn>
        </TwoColumn>

        <MapSection>
          <MapHeading>Visit Us</MapHeading>
          <MapWrapper>
            <iframe
              title="Spurnest Techlabs Office Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3665.174760948085!2d72.66829767576672!3d23.273098806931852!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x395c2d4751bf8e6f%3A0x7a43e96a538cc5f8!2sSiddheshwari%20Heights!5e0!3m2!1sen!2sin!4v1739738820115!5m2!1sen!2sin"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </MapWrapper>
        </MapSection>
      </Container>
      <Footer />
    </AnimationRevealPage>
  );
};
